/**
 * 「固定する商品 ID」の入力を、送れる形に整える(純粋関数)。
 *
 * 画面から貼られるのは、改行やカンマの混ざった人の手による文字列である。
 * ここで整えてから送るが、**判断の正本はサーバー側**(起動口)であり、
 * さらに ETL 側が「承認済みの上位 200 商品か」を確かめる。ここで通ったことは
 * 取り込めることの保証ではない。
 *
 * それでも画面で検査するのは、押してから断られる往復を減らすためである。
 * したがって**条件はサーバーと同じ**にする。片方だけ緩いと、押せるのに必ず
 * 失敗する入力ができてしまう。
 *
 * - 形: `server/src/presentation/graphql/admin-graphql-operator-fill-resolvers.ts`
 *   の `PRODUCT_ID_PATTERN` と同じ(その正本は ETL の `SAFE_PATH_SEGMENT_PATTERN`)
 * - 重複: 落とす。順序は貼られたまま保つ
 * - 件数: 1 回の件数(`limit`)以下。上限そのものは API の定数(30)が正本
 */

/** 商品 ID の形。ETL / API と同じ。 */
const PRODUCT_ID_PATTERN = /^[0-9A-Za-z][0-9A-Za-z._-]{0,63}$/;

export interface ProductIdsParseResult {
  /** 送る値。空配列なら「固定しない」(従来どおりの選び方に任せる)。 */
  readonly ids: readonly string[];
  /** 画面に出す断り。null なら送ってよい。 */
  readonly error: string | null;
}

/**
 * 改行・カンマ・空白区切りの入力を商品 ID の並びにする。
 *
 * @param text 入力欄の中身(空なら固定しない)
 * @param limit その実行の件数。これを超える指定は断る
 */
export function parseProductIds(
  text: string,
  limit: number,
): ProductIdsParseResult {
  const tokens = text
    .split(/[\s,、]+/)
    .map((token) => token.trim())
    .filter((token) => token.length > 0);

  if (tokens.length === 0) return { ids: [], error: null };

  const invalid = tokens.filter((token) => !PRODUCT_ID_PATTERN.test(token));
  if (invalid.length > 0) {
    return {
      ids: [],
      // 何が弾かれたかを出す。先頭の 3 件までに留める(貼り間違いの多くは同じ形)
      error: `商品 ID の形が不正です: ${invalid.slice(0, 3).join(", ")}。英数字で始まり、英数字・ピリオド・アンダースコア・ハイフンだけで 64 文字以内にしてください。`,
    };
  }

  const unique: string[] = [];
  for (const token of tokens) {
    if (!unique.includes(token)) unique.push(token);
  }

  if (unique.length > limit) {
    return {
      ids: [],
      error: `固定する商品は ${String(limit)} 件までです（いまは ${String(unique.length)} 件）。件数を合わせてください。`,
    };
  }

  return { ids: unique, error: null };
}
